// src/components/ServiceCards/ServiceCards.tsx
import React from 'react';
import BookNowButton from '../BookNowButton/BookNowButton.tsx';
import './ServiceCards.css';

interface Service {
  icon: string;
  title: string;
  description: string;
  price: string;
  priceValue: string;
  featured?: boolean;
  schemaId: string;
  serviceType: string;
  alternateName?: string;
}

const ServiceCards: React.FC = () => {
  const services: Service[] = [
    {
      icon: '🌟',
      title: 'Whole Life Vedic Reading',
      description: 'Comprehensive Vedic astrology analysis of your natal chart revealing your personality, strengths, and life path based on your exact birth time and location. Ideal for anyone seeking deep kundali and jyotish insight. Available online worldwide.',
      price: '$60',
      priceValue: '60.00',
      featured: false,
      schemaId: 'whole-life-reading',
      serviceType: 'Vedic Astrology Reading',
      alternateName: 'Kundali Reading'
    },
    {
      icon: '💫',
      title: 'Relationship Compatibility Reading',
      description: 'Explore the cosmic connection between you and your partner through Vedic synastry and relationship astrology. Understand your compatibility, karmic ties, and long-term potential through kundali matching.',
      price: '$25',
      priceValue: '25.00',
      featured: false,
      schemaId: 'relationship-reading',
      serviceType: 'Relationship Astrology Reading',
      alternateName: 'Kundali Matching'
    },
    {
      icon: '🔮',
      title: 'Career & Finance Reading',
      description: 'Discover your professional calling and optimal career timing through Vedic astrological analysis of your midheaven and planetary transits. Gain clarity on financial growth cycles with a personalised jyotish career reading.',
      price: '$25',
      priceValue: '25.00',
      schemaId: 'career-finance-reading',
      serviceType: 'Career Astrology Reading'
    },
    {
      icon: '⏳',
      title: 'Birth Time Rectification',
      description: 'Your birth time is the foundation of your entire Vedic astrological blueprint. Even a few minutes off can shift your Ascendant and planetary periods (dashas). Pinpoint your exact birth time through meticulous jyotish analysis of your key life events.',
      price: '$60',
      priceValue: '60.00',
      schemaId: 'birth-time-rectification',
      serviceType: 'Birth Time Rectification'
    },
    {
      icon: '🏠',
      title: 'Vaastu Shastra Consultation',
      description: 'Harmonise your living or workspace with the ancient Vedic science of Vaastu Shastra, aligning the five elements and eight directions to restore flow, prosperity, and wellbeing in your home or office. Remote consultations available across the USA.',
      price: '$150',
      priceValue: '150.00',
      schemaId: 'vaastu-shastra',
      serviceType: 'Vaastu Shastra Consultation',
      alternateName: 'Vastu Shastra Consultation'
    }
  ];

  const handleBookNowClick = (serviceName: string) => {
    console.log(`Book Now clicked for: ${serviceName}`);
  };

  return (
    <section
      id="services"
      className="services-section"
      aria-label="Vedic Astrology Services by Astrologer Kavitha Nandy"
      itemScope
      itemType="https://schema.org/ItemList"
    >
      <meta itemProp="name" content="Vedic Astrology Services by Astrologer Kavitha Nandy" />
      <meta itemProp="url" content="https://cosmickavi.com/services" />
      <div className="container">
        <h2 className="services-title">Vedic Astrology Services</h2>
        <div className="services-grid">
          {services.map((service, index) => (
            <div
              key={index}
              id={`service-${service.schemaId}`}
              className={`service-card ${service.featured ? 'featured' : ''}`}
              itemScope
              itemType="https://schema.org/Service"
              itemProp="itemListElement"
            >
              <meta itemProp="position" content={String(index + 1)} />
              <meta itemProp="serviceType" content={service.serviceType} />
              <meta itemProp="url" content={`https://cosmickavi.com/services#${service.schemaId}`} />
              {service.alternateName && (
                <meta itemProp="alternateName" content={service.alternateName} />
              )}
              <link itemProp="provider" href="https://cosmickavi.com/#person" />

              {service.featured && <div className="featured-badge">Most Popular</div>}

              <div className="service-content">
                <div>
                  <div className="service-icon" aria-hidden="true">{service.icon}</div>
                  <h3 itemProp="name">{service.title}</h3>
                  <p itemProp="description">{service.description}</p>
                </div>

                <div className="service-bottom">
                  <div
                    className="price"
                    itemScope
                    itemType="https://schema.org/Offer"
                    itemProp="offers"
                  >
                    <span itemProp="price" content={service.priceValue}>{service.price}</span>
                    <meta itemProp="priceCurrency" content="USD" />
                    <meta itemProp="availability" content="https://schema.org/InStock" />
                  </div>
                  <BookNowButton
                    variant="primary"
                    size="medium"
                    onClick={() => handleBookNowClick(service.title)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceCards;